import { useState, useEffect, useCallback, useRef } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import app from "../firebase/config"; // abc-dashboard's own Firebase app instance (default export)
import BankSyncFailuresTab from "./BankSyncFailuresTab";
import { fmtDT } from "../crm/dateFormat";

// One "Errors" screen, two inner tabs (see Security & Launch-Readiness
// Audit, Section 11):
//   System Errors   — frontend errors from the resident app's screens
//   Function Errors — backend Cloud Function errors (both projects)
// Both come from a single staff-gated Cloud Function, listErrors.

const functions = getFunctions(app, "asia-south1");
const listErrorsFn = httpsCallable(functions, "listErrors");
const listJobRunsFn = httpsCallable(functions, "listJobRuns");

// ── Instant open ─────────────────────────────────────────────────────────────
// Reading logs from Google takes a few seconds. So the last result is remembered here (in this browser
// tab only), shown at once when the screen opens, and refreshed quietly in the background. The CRM
// also fetches it as soon as you sign in (prefetchErrorsScreen), so the first open is usually instant too.
const errorsCache = new Map(); // "days|severity" -> { data, at }
const errorsInFlight = new Map(); // same key -> the request already running (no double fetching)
const jobsCache = { data: null, at: 0 };
let jobsInFlight = null;
const errorsKey = (days, sev) => `${days}|${sev}`;

function fetchErrors(days, sev) {
  const key = errorsKey(days, sev);
  if (errorsInFlight.has(key)) return errorsInFlight.get(key);
  const p = listErrorsFn({ days, minSeverity: sev })
    .then((res) => {
      errorsCache.set(key, { data: res.data, at: Date.now() });
      return res.data;
    })
    .finally(() => errorsInFlight.delete(key));
  errorsInFlight.set(key, p);
  return p;
}

function fetchJobs() {
  if (jobsInFlight) return jobsInFlight;
  jobsInFlight = listJobRunsFn()
    .then((res) => {
      jobsCache.data = res.data;
      jobsCache.at = Date.now();
      return res.data;
    })
    .finally(() => {
      jobsInFlight = null;
    });
  return jobsInFlight;
}

// Called by the app shell after sign-in. Failures are ignored here; the screen shows them if you open it.
export function prefetchErrorsScreen({ jobs = false } = {}) {
  const done = fetchErrors(7, "ERROR").then(() => {}, () => {});
  if (jobs) fetchJobs().catch(() => {});
  return done; // the menu waits for this, then counts the errors
}

// How many errors from the last 24 hours are in the remembered result. Fetches nothing new.
export function recentErrorCount() {
  const cached = errorsCache.get(errorsKey(7, "ERROR"));
  if (!cached || !cached.data) return 0;
  const since = Date.now() - 24 * 60 * 60 * 1000;
  const all = [...(cached.data.systemErrors || []), ...(cached.data.functionErrors || [])];
  return all.filter((r) => {
    const t = Date.parse(r && r.createdAt);
    return t && t >= since;
  }).length;
}

// Called on sign-out / user change so one person's results are never shown to the next person.
export function clearErrorsScreenCache() {
  errorsCache.clear();
  jobsCache.data = null;
  jobsCache.at = 0;
}

const fmtClock = (ms) => (ms ? new Date(ms).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "");

const RANGES = [
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
  { days: 0, label: "All time" },
];

const SHOW_LEN = 400; // on-screen preview only; the download has the full message

const card = { background: "#1a1a24", border: "1px solid #2d2d3a", borderRadius: "10px", padding: "14px" };
const muted = { color: "#555570", fontSize: "11px" };

function fmt(iso) {
  if (!iso) return "—";
  return fmtDT(iso);
}

function short(msg) {
  if (!msg) return msg;
  return msg.length > SHOW_LEN ? msg.slice(0, SHOW_LEN) + "…" : msg;
}

// Plain-text report of one error with everything we know about it - for "Copy report" and for pasting into a chat.
function buildReport(r, isSystem) {
  const lines = [];
  lines.push(isSystem ? "SYSTEM ERROR (resident app screen)" : "FUNCTION ERROR (backend)");
  lines.push(`Time: ${r.createdAt || "unknown"}`);
  if (isSystem) {
    lines.push(`Screen: ${r.screen || "unknown"}`);
    lines.push(`Society: ${r.societyId || "not recorded"}`);
    if (r.userId) lines.push(`User: ${r.userId}`);
    if (r.flatId) lines.push(`Flat: ${r.flatId}`);
  } else {
    lines.push(`Project: ${r.project || "unknown"}`);
    lines.push(`Function: ${r.functionName || "unknown"}`);
    if (r.severity) lines.push(`Severity: ${r.severity}`);
    if (r.kind) lines.push(`Type: ${r.kind === "audit" ? "Google Cloud audit record" : r.kind === "request" ? "Web request" : "Your code"}`);
  }
  lines.push("", "Message:", r.message || "(none)");
  if (r.context) lines.push("", "Context: " + Object.entries(r.context).map(([k, v]) => `${k}=${v}`).join(", "));
  if (r.details) lines.push("", "Details:", JSON.stringify(r.details, null, 2));
  return lines.join("\n");
}

// "Show full details" + "Copy report" under one error.
function ErrorDetails({ row, isSystem }) {
  const [copied, setCopied] = useState(false);
  const report = buildReport(row, isSystem);
  async function copy() {
    try {
      await navigator.clipboard.writeText(report);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      window.prompt("Copy this report (Ctrl+C):", report);
    }
  }
  return (
    <details style={{ marginTop: "8px" }}>
      <summary style={{ ...muted, cursor: "pointer" }}>Show full details</summary>
      <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "11px", color: "#aab2c5", margin: "8px 0", fontFamily: "inherit" }}>{report}</pre>
      <button
        onClick={copy}
        style={{ background: "#1a1a24", color: "#fff", border: "1px solid #2d2d3a", borderRadius: "8px", padding: "5px 12px", cursor: "pointer", fontSize: "12px" }}
      >
        {copied ? "Copied" : "Copy report"}
      </button>
    </details>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "8px 16px", borderRadius: "8px", cursor: "pointer", fontSize: "13px",
        border: "1px solid " + (active ? "#6c5ce7" : "#2d2d3a"),
        background: active ? "#6c5ce7" : "#1a1a24",
        color: "#fff",
      }}
    >
      {children}
    </button>
  );
}

const STATE_STYLE = {
  success: { color: "#00c853", label: "Success" },
  partly_failed: { color: "#ffb74d", label: "Partly failed" },
  failed: { color: "#ff5252", label: "Failed" },
  not_run: { color: "#ff5252", label: "Not run" },
  no_scheduled_yet: { color: "#8a8fa8", label: "No scheduled run recorded yet" },
};
const RUN_TYPE_LABEL = { scheduled: "Scheduled", manual: "Manual" };
const JOB_SHORT_LABEL = { crmBackup: "CRM backup", billingBackup: "Billing backup" };

function fmtSize(bytes) {
  if (typeof bytes !== "number") return "";
  return bytes < 1048576 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1048576).toFixed(1)} MB`;
}

// One line per database of a backup run: project / database, the Google account, size, and the error if it failed.
function RunDetails({ details }) {
  if (!Array.isArray(details) || details.length === 0) return null;
  return (
    <div style={{ marginTop: "6px", display: "flex", flexDirection: "column", gap: "3px" }}>
      {details.map((d, i) => {
        const ok = d.status === "ok";
        return (
          <div key={i} style={{ fontSize: "12px", color: ok ? "#aab2c5" : "#ff5252", wordBreak: "break-word" }}>
            <span style={{ color: ok ? "#00c853" : "#ff5252" }}>{ok ? "✓" : "✗"}</span>{" "}
            {d.projectId ? (
              <>
                <span style={{ color: "#fff" }}>{d.projectId}</span> / {d.databaseId}
              </>
            ) : (
              "Account problem"
            )}
            <span style={muted}>
              {" "}· {d.account}
              {d.sizeBytes ? ` · ${fmtSize(d.sizeBytes)}` : ""}
            </span>
            {d.error ? <div style={{ fontSize: "11px", marginTop: "2px" }}>{d.error}</div> : null}
          </div>
        );
      })}
    </div>
  );
}

// Third tab: Scheduled Jobs. Owner-only on the server (listJobRuns).
function JobsTab() {
  const [data, setData] = useState(jobsCache.data);
  const [loading, setLoading] = useState(!jobsCache.data);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    const haveOld = !!jobsCache.data;
    if (haveOld) {
      setData(jobsCache.data); // show the remembered result straight away
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setErr("");
    try {
      setData(await fetchJobs());
    } catch (e) {
      console.error("Fetch scheduled jobs failed:", e);
      if (e && e.code === "functions/permission-denied") {
        setErr("Only the Owner can see Scheduled Jobs.");
      } else if (!haveOld) {
        setErr("Couldn't load Scheduled Jobs. Please contact the developer.");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const jobs = data?.jobs || [];
  const recent = data?.recent || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <div style={{ ...muted, flex: 1 }}>
          Latest status of each scheduled job. A red "Not run" means no scheduled (timer) run for more than 26 hours; manual runs do not count.
        </div>
        {refreshing && <span style={muted}>Updating…</span>}
        {!refreshing && jobsCache.at > 0 && <span style={muted}>Updated {fmtClock(jobsCache.at)}</span>}
        <button
          onClick={load}
          style={{ background: "#1a1a24", color: "#fff", border: "1px solid #2d2d3a", borderRadius: "8px", padding: "8px 14px", cursor: "pointer" }}
        >
          Refresh
        </button>
      </div>

      {(data?.warnings || []).map((w) => (
        <div key={w} style={{ color: "#ffb74d", fontSize: "12px" }}>⚠ {w}</div>
      ))}

      {loading && <div style={{ color: "#aab2c5", padding: "12px 0" }}>Loading…</div>}
      {!loading && err && <div style={{ color: "#ff5252", padding: "12px 0" }}>{err}</div>}
      {!loading && !err && jobs.length === 0 && (
        <div style={{ color: "#aab2c5", padding: "12px 0" }}>No job runs recorded yet.</div>
      )}

      {!loading && !err && jobs.map((j) => {
        const st = STATE_STYLE[j.state] || { color: "#8a8fa8", label: j.state };
        return (
          <div key={j.jobName} style={{ ...card, borderColor: j.state === "not_run" || j.state === "failed" ? "#ff5252" : "#2d2d3a" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "#fff", fontSize: "14px" }}>
              <span>{j.label}</span>
              <span style={{ color: st.color, fontWeight: 600, fontSize: "13px" }}>{st.label}</span>
            </div>
            {j.lastRun ? (
              <div style={{ color: "#aab2c5", fontSize: "13px", marginTop: "8px", wordBreak: "break-word" }}>
                Last run: {fmt(j.lastRun.startedAt)} · {RUN_TYPE_LABEL[j.lastRun.runType] || j.lastRun.runType}
                {j.lastRun.summary ? ` · ${j.lastRun.summary}` : ""}
              </div>
            ) : (
              <div style={{ color: "#aab2c5", fontSize: "13px", marginTop: "8px" }}>Last run: none recorded</div>
            )}
            <RunDetails details={j.lastRun && j.lastRun.details} />
            <div style={{ ...muted, marginTop: "6px" }}>
              Last scheduled run: {j.lastScheduledRun ? fmt(j.lastScheduledRun.startedAt) : "none yet"}
            </div>
            {j.info && <div style={{ ...muted, marginTop: "4px" }}>{j.info}</div>}
            {j.state === "not_run" && (
              <div style={{ color: "#ff5252", fontSize: "12px", marginTop: "6px" }}>
                No scheduled run for {j.hoursSinceScheduled} hours.
              </div>
            )}
          </div>
        );
      })}

      {!loading && !err && recent.length > 0 && (
        <div style={{ ...card }}>
          <div style={{ color: "#fff", fontSize: "13px", marginBottom: "8px" }}>Recent runs</div>
          {recent.map((r) => {
            const st = STATE_STYLE[r.status] || { color: "#8a8fa8", label: r.status };
            return (
              <div key={r.id} style={{ padding: "4px 0" }}>
                <div style={{ display: "flex", gap: "10px", fontSize: "12px", color: "#aab2c5", flexWrap: "wrap" }}>
                  <span style={{ minWidth: "150px" }}>{fmt(r.startedAt)}</span>
                  <span style={{ minWidth: "100px", color: "#fff" }}>{JOB_SHORT_LABEL[r.jobName] || r.jobName}</span>
                  <span style={{ minWidth: "70px" }}>{RUN_TYPE_LABEL[r.runType] || r.runType}</span>
                  <span style={{ minWidth: "90px", color: st.color }}>{st.label}</span>
                  <span style={{ flex: 1, wordBreak: "break-word" }}>{r.summary}</span>
                </div>
                {Array.isArray(r.details) && r.details.length > 0 && (
                  <details style={{ marginLeft: "4px" }}>
                    <summary style={{ ...muted, cursor: "pointer" }}>Show databases ({r.details.length})</summary>
                    <RunDetails details={r.details} />
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ErrorsScreen() {
  const [tab, setTab] = useState("system");
  const [days, setDays] = useState(7);
  const [minSeverity, setMinSeverity] = useState("ERROR");
  const firstCached = errorsCache.get(errorsKey(7, "ERROR"));
  const [data, setData] = useState(firstCached ? firstCached.data : null);
  const [loading, setLoading] = useState(!firstCached);
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(firstCached ? firstCached.at : 0);
  const [err, setErr] = useState("");
  const latestRequest = useRef(0);

  const load = useCallback(async (d, sev) => {
    const mine = ++latestRequest.current; // so a slow older answer can never overwrite a newer one
    const cached = errorsCache.get(errorsKey(d, sev));
    if (cached) {
      setData(cached.data); // show the remembered result straight away
      setUpdatedAt(cached.at);
      setLoading(false);
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setErr("");
    try {
      const fresh = await fetchErrors(d, sev);
      if (mine !== latestRequest.current) return;
      setData(fresh);
      setUpdatedAt(Date.now());
    } catch (e) {
      console.error("Fetch errors failed:", e);
      if (mine === latestRequest.current && !cached) setErr("Couldn't load errors. Please contact the developer.");
    } finally {
      if (mine === latestRequest.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => { load(days, minSeverity); }, [days, minSeverity, load]);

  const systemErrors = data?.systemErrors || [];
  const functionErrors = data?.functionErrors || [];
  const rows = tab === "system" ? systemErrors : functionErrors;

  function downloadErrors() {
    const payload = {
      exportedAt: new Date().toISOString(),
      tab: tab === "system" ? "System Errors" : "Function Errors",
      range: RANGES.find((r) => r.days === days)?.label || String(days),
      count: rows.length,
      errors: rows,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${tab}-errors-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "12px" }}>
      <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
        <TabButton active={tab === "system"} onClick={() => setTab("system")}>
          System Errors{data ? ` (${systemErrors.length})` : ""}
        </TabButton>
        <TabButton active={tab === "function"} onClick={() => setTab("function")}>
          Function Errors{data ? ` (${functionErrors.length})` : ""}
        </TabButton>
        <TabButton active={tab === "jobs"} onClick={() => setTab("jobs")}>
          Scheduled Jobs
        </TabButton>
        <TabButton active={tab === "banksync"} onClick={() => setTab("banksync")}>
          Bank Sync
        </TabButton>
        <div style={{ flex: 1 }} />
        {tab !== "jobs" && tab !== "banksync" && (refreshing || updatedAt > 0) && (
          <span style={muted}>{refreshing ? "Updating…" : `Updated ${fmtClock(updatedAt)}`}</span>
        )}
        {tab !== "jobs" && tab !== "banksync" && (<>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          style={{ background: "#1a1a24", color: "#fff", border: "1px solid #2d2d3a", borderRadius: "8px", padding: "8px" }}
        >
          {RANGES.map((r) => <option key={r.days} value={r.days}>{r.label}</option>)}
        </select>
        {tab === "function" && (
          <select
            value={minSeverity}
            onChange={(e) => setMinSeverity(e.target.value)}
            style={{ background: "#1a1a24", color: "#fff", border: "1px solid #2d2d3a", borderRadius: "8px", padding: "8px" }}
          >
            <option value="ERROR">Errors only</option>
            <option value="WARNING">Errors + warnings</option>
          </select>
        )}
        <button
          onClick={() => load(days, minSeverity)}
          style={{ background: "#1a1a24", color: "#fff", border: "1px solid #2d2d3a", borderRadius: "8px", padding: "8px 14px", cursor: "pointer" }}
        >
          Refresh
        </button>
        <button
          onClick={downloadErrors}
          disabled={loading || rows.length === 0}
          style={{
            background: "#1a1a24", color: "#fff", border: "1px solid #2d2d3a", borderRadius: "8px", padding: "8px 14px",
            cursor: loading || rows.length === 0 ? "not-allowed" : "pointer",
            opacity: loading || rows.length === 0 ? 0.5 : 1,
          }}
        >
          Download
        </button>
        </>)}
      </div>

      {tab === "jobs" && <JobsTab />}
      {tab === "banksync" && <BankSyncFailuresTab />}
      {tab !== "jobs" && tab !== "banksync" && (<>
      <div style={muted}>
        {tab === "system"
          ? "Errors from the resident app's screens (e.g. Dashboard failed to load), grouped by society."
          : "Errors from backend Cloud Functions in abc-saas-prod and abc-dashboard001 (logins, backups, scheduled jobs)."}
      </div>

      {(data?.warnings || []).map((w) => (
        <div key={w} style={{ color: "#ffb74d", fontSize: "12px" }}>⚠ {w}</div>
      ))}

      {loading && <div style={{ color: "#aab2c5", padding: "12px 0" }}>Loading…</div>}
      {!loading && err && <div style={{ color: "#ff5252", padding: "12px 0" }}>{err}</div>}
      {!loading && !err && rows.length === 0 && (
        <div style={{ color: "#aab2c5", padding: "12px 0" }}>
          No {tab === "system" ? "system" : "function"} errors in this period. 🎉
        </div>
      )}

      {!loading && !err && rows.map((r) =>
        tab === "system" ? (
          <div key={r.id} style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", color: "#fff", fontSize: "13px" }}>
              <span>{r.screen || "Unknown screen"}</span>
              <span style={muted}>{fmt(r.createdAt)}</span>
            </div>
            <div style={{ color: "#ff8a80", fontSize: "13px", marginTop: "8px", wordBreak: "break-word" }}>{short(r.message)}</div>
            <div style={{ ...muted, marginTop: "8px" }}>
              Society: {r.societyId || "not recorded"}{r.userId ? ` · User: ${r.userId}` : ""}{r.flatId ? ` · Flat: ${r.flatId}` : ""}
            </div>
            <ErrorDetails row={r} isSystem />
          </div>
        ) : (
          <div key={r.id} style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", color: "#fff", fontSize: "13px" }}>
              <span>{r.functionName || "Unknown function"} <span style={muted}>· {r.project}{r.kind === "audit" ? " · Google Cloud record" : r.kind === "request" ? " · web request" : ""}</span></span>
              <span style={muted}>{fmt(r.createdAt)}</span>
            </div>
            <div style={{ color: "#ff8a80", fontSize: "13px", marginTop: "8px", wordBreak: "break-word" }}>{short(r.message)}</div>
            {r.context && (
              <div style={{ ...muted, marginTop: "8px", wordBreak: "break-word" }}>
                {Object.entries(r.context).map(([k, v]) => `${k}: ${v}`).join(" · ")}
              </div>
            )}
            <ErrorDetails row={r} isSystem={false} />
          </div>
        )
      )}
      </>)}
    </div>
  );
}